import React, { useEffect, useMemo, useRef, useState } from "react"
import { useQuery, useSubscription, useMutation } from "@apollo/client"
import {
  Button,
  Input,
  Nav,
  NavItem,
  NavLink,
  Spinner,
} from "reactstrap"
import classnames from "classnames"
import {
  ORDER_CHAT_META,
  ORDER_MESSAGES,
  ORDER_MESSAGE_ADDED,
  ORDER_TYPING,
  SEND_ORDER_MESSAGE,
  channelLabel,
  SENDER_LABELS,
  OrderChatMetaResponse,
  OrderMessagesResponse,
  OrderMessageAddedResponse,
  OrderTypingResponse,
  OrderMessage,
} from "../../domain/graphql/chat"

const VIEWER_TYPE = "ADMIN"

type Props = {
  orderId: string
  // 監視が主目的。送信UIを出すかどうか（既定: false = 読み取り専用）
  allowSend?: boolean
}

const formatTime = (iso: string): string => {
  if (!iso) return ""
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ""
  const mm = ("" + (d.getMonth() + 1)).padStart(2, "0")
  const dd = ("" + d.getDate()).padStart(2, "0")
  const hh = ("" + d.getHours()).padStart(2, "0")
  const mi = ("" + d.getMinutes()).padStart(2, "0")
  return `${mm}/${dd} ${hh}:${mi}`
}

const senderLabel = (senderType: string): string =>
  SENDER_LABELS[senderType] || senderType

// 相手の既読状態を読みやすい文字列に
const readByText = (m: OrderMessage): string => {
  const read: string[] = []
  if (m.readByUser) read.push("お客様")
  if (m.readByRider) read.push("配達員")
  if (m.readByStore) read.push("店舗")
  return read.length ? `既読: ${read.join("・")}` : ""
}

const MessageBubble: React.FC<{ message: OrderMessage }> = ({ message }) => {
  const read = readByText(message)
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: "#8898aa", marginBottom: 2 }}>
        <span style={{ fontWeight: 600, color: "#525f7f" }}>
          {message.senderName || senderLabel(message.senderType)}
        </span>
        <span style={{ marginLeft: 6 }}>{senderLabel(message.senderType)}</span>
        <span style={{ marginLeft: 8 }}>{formatTime(message.createdAt)}</span>
      </div>
      <div
        style={{
          display: "inline-block",
          maxWidth: "80%",
          padding: "8px 12px",
          borderRadius: 12,
          backgroundColor: "#f6f9fc",
          border: "1px solid #e9ecef",
          wordBreak: "break-word",
        }}
      >
        {message.text && (
          <div style={{ whiteSpace: "pre-wrap" }}>{message.text}</div>
        )}
        {message.image && (
          <a href={message.image} target="_blank" rel="noopener noreferrer">
            <img
              src={message.image}
              alt="添付"
              style={{
                marginTop: message.text ? 6 : 0,
                maxWidth: 200,
                maxHeight: 200,
                borderRadius: 8,
                display: "block",
              }}
            />
          </a>
        )}
      </div>
      {read && (
        <div style={{ fontSize: 11, color: "#adb5bd", marginTop: 2 }}>{read}</div>
      )}
    </div>
  )
}

const OrderChat: React.FC<Props> = ({ orderId, allowSend = false }) => {
  const [activeChannel, setActiveChannel] = useState<string>("")
  // _id をキーにメッセージを保持（subscription での重複を防ぐ）
  const [messages, setMessages] = useState<OrderMessage[]>([])
  const [typing, setTyping] = useState<Record<string, OrderTypingResponse["orderTyping"]>>({})
  const [text, setText] = useState("")
  const [image, setImage] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const upsertMessages = (incoming: OrderMessage[]) => {
    setMessages(prev => {
      const map = new Map<string, OrderMessage>()
      for (const m of prev) map.set(m._id, m)
      for (const m of incoming) {
        const existing = map.get(m._id)
        map.set(m._id, existing ? { ...existing, ...m } : m)
      }
      return Array.from(map.values()).sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      )
    })
  }

  const metaResult = useQuery<OrderChatMetaResponse>(ORDER_CHAT_META, {
    variables: { orderId },
    skip: !orderId,
  })

  // CI(react-scripts build)は警告をエラー扱いするため、毎レンダーで新配列を作らないよう useMemo 化
  // (react-hooks/exhaustive-deps: 下の useEffect 依存配列を安定させる)
  const channels = useMemo(() => metaResult.data?.orderChatMeta?.channels || [], [metaResult.data])

  useEffect(() => {
    if (channels.length && !activeChannel) {
      setActiveChannel(channels[0])
    }
  }, [channels, activeChannel])

  // 全閲覧可チャンネルのメッセージを一括取得（channel 省略）
  const messagesResult = useQuery<OrderMessagesResponse>(ORDER_MESSAGES, {
    variables: { orderId },
    skip: !orderId,
    fetchPolicy: "network-only",
    onCompleted: data => {
      if (data?.orderMessages) upsertMessages(data.orderMessages)
    },
  })

  // 新着メッセージ購読（重複は _id で排除）
  useSubscription<OrderMessageAddedResponse>(ORDER_MESSAGE_ADDED, {
    variables: { orderId, viewerType: VIEWER_TYPE },
    skip: !orderId,
    onSubscriptionData: ({ subscriptionData }) => {
      const added = subscriptionData.data?.orderMessageAdded
      if (added) {
        upsertMessages([
          {
            ...added,
            readByUser: "",
            readByRider: "",
            readByStore: "",
          } as OrderMessage,
        ])
      }
    },
  })

  // 入力中購読
  useSubscription<OrderTypingResponse>(ORDER_TYPING, {
    variables: { orderId, viewerType: VIEWER_TYPE },
    skip: !orderId,
    onSubscriptionData: ({ subscriptionData }) => {
      const t = subscriptionData.data?.orderTyping
      if (!t) return
      const key = `${t.channel}:${t.senderType}`
      setTyping(prev => {
        const next = { ...prev }
        if (t.isTyping) next[key] = t
        else delete next[key]
        return next
      })
    },
  })

  const [sendMessage, sendState] = useMutation(SEND_ORDER_MESSAGE, {
    onCompleted: () => {
      setText("")
      setImage(null)
      if (fileInputRef.current) fileInputRef.current.value = ""
    },
    onError: e => {
      window.alert(e.message)
    },
  })

  const visibleMessages = useMemo(
    () => messages.filter(m => m.channel === activeChannel),
    [messages, activeChannel],
  )

  const typingInChannel = useMemo(
    () =>
      Object.values(typing).filter(
        t => t.channel === activeChannel && t.senderType !== VIEWER_TYPE,
      ),
    [typing, activeChannel],
  )

  // 新着で最下部へスクロール
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
  }, [visibleMessages.length, activeChannel])

  const onSend = () => {
    if (!activeChannel) return
    if (!text.trim() && !image) return
    sendMessage({
      variables: {
        orderId,
        channel: activeChannel,
        text: text.trim() || null,
        image: image || null,
      },
    })
  }

  if (!orderId) return null

  if (metaResult.loading) {
    return (
      <div style={{ padding: 16, textAlign: "center" }}>
        <Spinner size="sm" /> 読み込み中…
      </div>
    )
  }

  if (metaResult.error) {
    return (
      <div style={{ padding: 16, color: "#f5365c" }}>
        チャット情報の取得に失敗しました: {metaResult.error.message}
      </div>
    )
  }

  if (!channels.length) {
    return <div style={{ padding: 16, color: "#8898aa" }}>チャンネルがありません。</div>
  }

  return (
    <div>
      {/* チャンネルタブ（1件なら非表示） */}
      {channels.length > 1 && (
        <Nav tabs style={{ marginBottom: 8 }}>
          {channels.map(ch => (
            <NavItem key={ch}>
              <NavLink
                href="#"
                className={classnames({ active: ch === activeChannel })}
                style={{ cursor: "pointer" }}
                onClick={e => {
                  e.preventDefault()
                  setActiveChannel(ch)
                }}
              >
                {channelLabel(ch)}
              </NavLink>
            </NavItem>
          ))}
        </Nav>
      )}

      {/* メッセージ一覧 */}
      <div
        ref={listRef}
        style={{
          height: 360,
          overflowY: "auto",
          padding: 12,
          border: "1px solid #e9ecef",
          borderRadius: 8,
          backgroundColor: "#fff",
        }}
      >
        {messagesResult.loading && !messages.length ? (
          <div style={{ textAlign: "center", color: "#8898aa" }}>
            <Spinner size="sm" /> 読み込み中…
          </div>
        ) : visibleMessages.length ? (
          visibleMessages.map(m => <MessageBubble key={m._id} message={m} />)
        ) : (
          <div style={{ textAlign: "center", color: "#8898aa", paddingTop: 24 }}>
            メッセージはありません。
          </div>
        )}

        {typingInChannel.length > 0 && (
          <div style={{ fontSize: 12, color: "#8898aa", fontStyle: "italic" }}>
            {typingInChannel
              .map(t => t.senderName || senderLabel(t.senderType))
              .join("、")}
            さんが入力中…
          </div>
        )}
      </div>

      {/* コンポーザ（任意・既定は監視のみ） */}
      {allowSend && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: "flex", gap: 8 }}>
            <Input
              type="textarea"
              rows={2}
              placeholder={`${channelLabel(activeChannel)} へメッセージを送信（管理）`}
              value={text}
              onChange={e => setText(e.target.value)}
            />
            <Button
              color="primary"
              disabled={sendState.loading || (!text.trim() && !image)}
              onClick={onSend}
              style={{ whiteSpace: "nowrap" }}
            >
              {sendState.loading ? <Spinner size="sm" /> : "送信"}
            </Button>
          </div>
          <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={e => setImage(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
            />
            {image && (
              <span style={{ fontSize: 12, color: "#8898aa" }}>{image.name}</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default OrderChat
